import { useState } from 'react';
import { ChevronDown, ChevronUp, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useJobProgress } from '@/context/job-progress-context';
import { useRetryFailedStoresMutation } from '@/services/store-conciliation';
import { useRetryStoreMutation } from '@/services/store/store';
import { cn } from '@/lib/utils';

const MAX_VISIBLE_ERRORS = 5;

export interface FailedStore {
  storeCode: number;
  error: string;
}

interface FailedStoresSectionProps {
  failedStores: FailedStore[];
  showErrors: boolean;
  onToggle: () => void;
  maxVisible?: number;
  canRetry?: boolean;
  conciliationDate?: string;
}

export const FailedStoresSection = ({
  failedStores,
  showErrors,
  onToggle,
  maxVisible = MAX_VISIBLE_ERRORS,
  canRetry = false,
  conciliationDate,
}: FailedStoresSectionProps) => {
  const { startPolling, refetch } = useJobProgress();
  const [retryFailedStores, { isLoading: isRetryingAll }] = useRetryFailedStoresMutation();
  const [retryStore] = useRetryStoreMutation();
  const [retryingStore, setRetryingStore] = useState<number | null>(null);

  const handleRetryAll = async () => {
    try {
      await retryFailedStores().unwrap();
      startPolling();
      toast({
        title: 'Reprocessamento iniciado',
        description: 'As lojas com falha estão sendo reprocessadas.',
      });
    } catch {
      toast({
        title: 'Erro',
        description: 'Falha ao reprocessar as lojas com erro. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleRetryStore = async (storeCode: number) => {
    if (!conciliationDate) return;

    setRetryingStore(storeCode);

    try {
      const result = await retryStore({ storeCode, date: conciliationDate }).unwrap();

      if (result.retried) {
        startPolling();
        toast({
          title: 'Reprocessamento iniciado',
          description: `A loja ${storeCode} está sendo reprocessada.`,
        });
      } else {
        refetch();
        toast({
          title: 'Loja não está mais em aberto',
          description: `A loja ${storeCode} foi removida da lista de falhas sem reprocessar.`,
        });
      }
    } catch {
      toast({
        title: 'Erro',
        description: `Falha ao reprocessar a loja ${storeCode}. Tente novamente.`,
        variant: 'destructive',
      });
    } finally {
      setRetryingStore(null);
    }
  };

  return (
  <div>
    <div className="flex items-center justify-between">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
      >
        {showErrors ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {showErrors ? 'Ocultar erros' : 'Ver lojas com erro'}
      </button>

      {canRetry && (
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={handleRetryAll}
          disabled={isRetryingAll}
        >
          <RotateCw className={cn('h-3 w-3 mr-1', isRetryingAll && 'animate-spin')} />
          Reprocessar Falhadas
        </Button>
      )}
    </div>

    {showErrors && (
      <div className="mt-2 p-2 bg-red-50 rounded max-h-24 overflow-y-auto">
        <ul className="text-xs space-y-1">
          {failedStores.slice(0, maxVisible).map((failure) => (
            <li key={failure.storeCode} className="text-red-700 flex items-center justify-between gap-2">
              <span>
                <strong>Loja {failure.storeCode}:</strong> {failure.error}
              </span>
              {canRetry && conciliationDate && (
                <button
                  onClick={() => handleRetryStore(failure.storeCode)}
                  disabled={retryingStore === failure.storeCode}
                  className="flex items-center gap-1 text-blue-600 hover:underline shrink-0 disabled:opacity-50"
                  title={`Reprocessar loja ${failure.storeCode}`}
                >
                  <RotateCw className={cn('h-3 w-3', retryingStore === failure.storeCode && 'animate-spin')} />
                  Reprocessar
                </button>
              )}
            </li>
          ))}
          {failedStores.length > maxVisible && (
            <li className="text-red-500 italic">
              + {failedStores.length - maxVisible} erros adicionais
            </li>
          )}
        </ul>
      </div>
    )}
  </div>
  );
};
